import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Public (no auth) account-deletion request page — the web counterpart of
 * the backend's /core/delete-account/ HTML page, for people who can't log in
 * anymore. Requests are queued for manual review, never executed instantly.
 */
@Component({
  selector: 'app-account-deletion',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './account-deletion.component.html',
  styleUrl: './account-deletion.component.scss'
})
export class AccountDeletionComponent {
  form: FormGroup;
  loading = false;
  submitted = false;
  errorMessage: string | null = null;

  constructor(private fb: FormBuilder, private http: HttpClient) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      reason: ['', [Validators.maxLength(1000)]],
    });
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.loading) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = null;

    try {
      await firstValueFrom(
        this.http.post(`${environment.SERVER_URL}/core/delete-account/submit/`, {
          email: this.form.value.email.trim(),
          reason: (this.form.value.reason || '').trim(),
        })
      );
      this.submitted = true;
    } catch (error) {
      const err = error as HttpErrorResponse;
      this.errorMessage =
        err.status === 429
          ? 'Too many requests. Please try again later.'
          : err.error?.error || 'Could not submit your request. Please try again.';
    } finally {
      this.loading = false;
    }
  }
}
